import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IconAlertCircle,
  IconLink,
  IconLoader2,
  IconRefresh,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import api from '../lib/api';

interface Contact {
  id: number;
  monday_id: number | null;
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  daily_reports: number;
  monthly_reports: number;
  signatory_contact: number;
  finance_contact: number;
  principal_contact: number;
  operational_contact: number;
  customer_care_contact: number;
  marketing_contact: number;
  it_contact: number;
  created_at: string;
  updated_at: string;
}

interface Distributor {
  id: number;
  i_customer: number;
  company_name: string;
  code: string;
}

interface UnlinkedContactsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function UnlinkedContactsDialog({ open, onClose }: UnlinkedContactsDialogProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [distributorsLoading, setDistributorsLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedDistributors, setSelectedDistributors] = useState<Distributor[]>([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [distributorSearch, setDistributorSearch] = useState('');

  const fetchUnlinkedContacts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/distributors/contacts/unlinked');
      setContacts(response.data?.data ?? []);
    } catch (err: any) {
      console.error('Failed to load unlinked contacts', err);
      setError(err.response?.data?.message || 'Failed to load unlinked contacts');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDistributors = useCallback(async (search?: string) => {
    setDistributorsLoading(true);

    try {
      const response = await api.get('/distributors', {
        params: {
          search: search || undefined,
          limit: 100,
          page: 1,
        },
      });
      setDistributors(response.data?.data ?? []);
    } catch (err) {
      console.error('Failed to load distributors', err);
    } finally {
      setDistributorsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchUnlinkedContacts();
      fetchDistributors();
    }
  }, [open, fetchUnlinkedContacts, fetchDistributors]);

  const filteredDistributors = useMemo(() => {
    const q = distributorSearch.trim().toLowerCase();
    if (!q) return distributors;
    return distributors.filter((d) =>
      `${d.company_name} ${d.i_customer}`.toLowerCase().includes(q),
    );
  }, [distributors, distributorSearch]);

  const handleLinkClick = (contact: Contact) => {
    setSelectedContact(contact);
    setSelectedDistributors([]);
    setSaveError(null);
    setDistributorSearch('');
    setLinkDialogOpen(true);
  };

  const handleDeleteClick = (contact: Contact) => {
    setSelectedContact(contact);
    setSaveError(null);
    setDeleteDialogOpen(true);
  };

  const handleLinkContact = async () => {
    if (!selectedContact) return;

    setSaving(true);
    setSaveError(null);

    try {
      await api.post('/distributors/contacts/link', {
        contactId: selectedContact.id,
        distributorICustomers: selectedDistributors.map((d) => d.i_customer),
      });

      setLinkDialogOpen(false);
      fetchUnlinkedContacts();
    } catch (err: any) {
      console.error('Failed to link contact', err);
      setSaveError(err.response?.data?.message || 'Failed to link contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;

    setSaving(true);
    setSaveError(null);

    try {
      await api.delete(`/distributors/contacts/${selectedContact.id}`);
      setDeleteDialogOpen(false);
      setSelectedContact(null);
      fetchUnlinkedContacts();
    } catch (err: any) {
      console.error('Failed to delete contact', err);
      setSaveError(err.response?.data?.message || 'Failed to delete contact');
    } finally {
      setSaving(false);
    }
  };

  const getContactRoles = (contact: Contact): string[] => {
    const roles: string[] = [];
    if (contact.principal_contact) roles.push('Principal');
    if (contact.signatory_contact) roles.push('Signatory');
    if (contact.finance_contact) roles.push('Finance');
    if (contact.operational_contact) roles.push('Operational');
    if (contact.customer_care_contact) roles.push('Customer Care');
    if (contact.marketing_contact) roles.push('Marketing');
    if (contact.it_contact) roles.push('IT');
    return roles;
  };

  const getReportTypes = (contact: Contact): string[] => {
    const reports: string[] = [];
    if (contact.daily_reports) reports.push('Daily');
    if (contact.monthly_reports) reports.push('Monthly');
    return reports;
  };

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
      >
        <SheetContent
          side="right"
          showCloseButton={false}
          className={cn(
            'gap-0 p-0 sm:max-w-none',
            'w-full !max-w-[min(100%,100vw)] sm:!w-[90vw] md:!w-[85vw]',
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 px-4 py-3">
            <div className="min-w-0 space-y-0.5">
              <SheetTitle className="text-base">Unlinked Contacts</SheetTitle>
              <SheetDescription>Contacts not linked to any distributor</SheetDescription>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={loading}
                      aria-label="Refresh"
                      onClick={fetchUnlinkedContacts}
                    />
                  }
                >
                  <IconRefresh className="size-4" />
                </TooltipTrigger>
                <TooltipContent>Refresh</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={onClose}
                      aria-label="Close unlinked contacts drawer"
                    />
                  }
                >
                  <IconX className="size-4" />
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <Separator />

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
            {loading && (
              <div className="flex justify-center py-8">
                <IconLoader2 className="size-8 animate-spin text-muted-foreground" />
              </div>
            )}

            {!loading && error && (
              <Alert variant="destructive" className="mb-2">
                <IconAlertCircle />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {!loading && !error && contacts.length === 0 && (
              <div className="rounded-lg border border-border p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  No unlinked contacts found. All contacts are linked to distributors.
                </p>
              </div>
            )}

            {!loading && !error && contacts.length > 0 && (
              <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-border">
                <Table className="text-[0.875rem]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[15%]">Name</TableHead>
                      <TableHead className="w-[18%] whitespace-normal">Email</TableHead>
                      <TableHead className="w-[12%]">Phone</TableHead>
                      <TableHead className="w-[25%] whitespace-normal">Roles</TableHead>
                      <TableHead className="w-[15%] whitespace-normal">Reports</TableHead>
                      <TableHead className="w-[15%] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell className="whitespace-normal">
                          {contact.first_name} {contact.last_name}
                        </TableCell>
                        <TableCell className="max-w-[18vw] whitespace-normal break-all">
                          {contact.email_address}
                        </TableCell>
                        <TableCell className="whitespace-normal">{contact.phone_number}</TableCell>
                        <TableCell className="whitespace-normal">
                          <div className="flex flex-wrap gap-1">
                            {getContactRoles(contact).map((role) => (
                              <Badge key={role} variant="outline" className="h-6 px-2 text-xs font-normal">
                                {role}
                              </Badge>
                            ))}
                            {getContactRoles(contact).length === 0 && (
                              <span className="text-xs text-muted-foreground">No roles</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-normal">
                          <div className="flex flex-wrap gap-1">
                            {getReportTypes(contact).map((report) => (
                              <Badge
                                key={report}
                                variant="outline"
                                className="h-6 border-primary/40 px-2 text-xs font-normal text-primary"
                              >
                                {report}
                              </Badge>
                            ))}
                            {getReportTypes(contact).length === 0 && (
                              <span className="text-xs text-muted-foreground">None</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-0.5">
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <Button
                                    variant="ghost"
                                    size="icon-xs"
                                    className="text-primary"
                                    onClick={() => handleLinkClick(contact)}
                                    aria-label="Link to distributor"
                                  />
                                }
                              >
                                <IconLink className="size-3.5" />
                              </TooltipTrigger>
                              <TooltipContent>Link to distributor</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <Button
                                    variant="ghost"
                                    size="icon-xs"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => handleDeleteClick(contact)}
                                    aria-label="Delete contact"
                                  />
                                }
                              >
                                <IconTrash className="size-3.5" />
                              </TooltipTrigger>
                              <TooltipContent>Delete contact</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Link Contact Dialog */}
      <Dialog
        open={linkDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setLinkDialogOpen(false);
        }}
      >
        <DialogContent showCloseButton={false} className="max-h-[min(90vh,720px)] gap-4 sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link Contact to Distributors</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            {saveError && (
              <Alert variant="destructive">
                <IconAlertCircle />
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            )}

            {selectedContact && (
              <p className="text-sm text-muted-foreground">
                Link{' '}
                <strong className="text-foreground">
                  {selectedContact.first_name} {selectedContact.last_name}
                </strong>{' '}
                to distributors
              </p>
            )}

            <div className="grid gap-2">
              <span className="text-sm font-medium leading-none">Select Distributors</span>
              <div className="relative">
                <Input
                  placeholder="Start typing to search..."
                  value={distributorSearch}
                  onChange={(e) => setDistributorSearch(e.target.value)}
                  disabled={distributorsLoading}
                  aria-label="Search distributors"
                />
                {distributorsLoading && (
                  <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2">
                    <IconLoader2 className="size-4 animate-spin text-muted-foreground" />
                  </span>
                )}
              </div>
            </div>

            {selectedDistributors.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selectedDistributors.map((d) => (
                  <Badge key={d.id} variant="secondary" className="gap-1 pr-1">
                    {d.company_name}
                    <button
                      type="button"
                      className="rounded-sm p-0.5 hover:bg-muted-foreground/20"
                      aria-label={`Remove ${d.company_name}`}
                      onClick={() =>
                        setSelectedDistributors((prev) => prev.filter((x) => x.id !== d.id))
                      }
                    >
                      <IconX className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            <ScrollArea className="h-48 rounded-md border border-border">
              <div className="p-2">
                {filteredDistributors.length === 0 && !distributorsLoading ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No distributors match your search.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {filteredDistributors.map((d) => {
                      const checked = selectedDistributors.some((x) => x.id === d.id);
                      return (
                        <li key={d.id}>
                          <label
                            className={cn(
                              'flex cursor-pointer items-start gap-2 rounded-md p-1.5 text-sm hover:bg-muted/50',
                              distributorsLoading && 'pointer-events-none opacity-50',
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(value) => {
                                if (value === true) {
                                  setSelectedDistributors((prev) =>
                                    prev.some((x) => x.id === d.id) ? prev : [...prev, d],
                                  );
                                } else {
                                  setSelectedDistributors((prev) =>
                                    prev.filter((x) => x.id !== d.id),
                                  );
                                }
                              }}
                              disabled={distributorsLoading}
                            />
                            <span className="leading-snug">
                              {d.company_name}{' '}
                              <span className="text-muted-foreground">({d.i_customer})</span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleLinkContact}
              disabled={saving || selectedDistributors.length === 0}
            >
              {saving ? (
                <IconLoader2 className="size-4 animate-spin" />
              ) : (
                <IconLink className="size-4" />
              )}
              Link Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteDialogOpen(false);
        }}
      >
        <DialogContent showCloseButton={false} className="gap-4">
          <DialogHeader>
            <DialogTitle>Delete Contact?</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Are you sure you want to delete{' '}
            <strong>
              {selectedContact?.first_name} {selectedContact?.last_name}
            </strong>
            ? This action cannot be undone.
          </p>
          {saveError && (
            <Alert variant="destructive">
              <IconAlertCircle />
              <AlertDescription>{saveError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteContact} disabled={saving}>
              {saving ? (
                <IconLoader2 className="size-4 animate-spin" />
              ) : (
                <IconTrash className="size-4" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
